import classNames from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import FJConstant from '../../../constants/FJConstant';
import FJModalClose from '../FJModalClose/FJModalClose';
import FJLocalStore from '../../../store/FJLocalStore';
import FJButton, { FJButtonType } from '../../../common/components/FJButton/FJButton';
import FJErrorIcon from '../../../assets/svg/cancel-white.svg?react';
import FJCheckIcon from '../../../assets/svg/check-white.svg?react';
import FJUtil from '../../../util/FJUtil';

import styles from './FJCloudTranscodingModal.module.less';

/**
 * 云转码进度弹窗组件
 * 显示转码进度、支持取消任务、多任务结果显示
 */
const FJCloudTranscodingModal = ({
    active,
    onClose,
    transcodingStatus,
    transcodingTasks,
    onCancelTask,
    onRestart,
    onConfirm,
    onReupload,
    inputOptions,
}) => {
    const [shouldShowProgress, setShouldShowProgress] = useState(true);
    const reuploadRef = useRef(null);
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        if (!active) {
            setShouldShowProgress(false);
            setProgress(0);
        } else {
            const totalProgress = transcodingTasks.reduce((acc, task) => acc + task.progress, 0);
            setProgress(totalProgress / transcodingTasks.length);
        }
    }, [active, transcodingTasks]);

    // 取消当前任务
    const handleCancelTask = useCallback(() => {
        onCancelTask?.(transcodingTasks.map(task => task.id).filter(id => !!id));
    }, [onCancelTask, transcodingTasks]);

    // 重新开始转码
    const handleRestart = useCallback(() => {
        onRestart?.();
    }, [onRestart]);

    const handleClose = useCallback(() => {
        setShouldShowProgress(false);
        onClose?.();
        if (transcodingStatus === FJConstant.PARTIAL_SUCCESS) {
            onConfirm?.();
        } else if (transcodingStatus === FJConstant.DEFAULT || transcodingStatus === FJConstant.RUNNING) {
            onCancelTask?.(transcodingTasks.map(task => task.id));
        }
    }, [onCancelTask, onClose, onConfirm, transcodingStatus, transcodingTasks]);

    const handleReupload = useCallback(() => {
        reuploadRef.current.click();
    }, []);

    const handleReuploadChange = useCallback(
        e => {
            const files = e.target.files;
            onReupload?.(files);
            reuploadRef.current.value = '';
        },
        [onReupload],
    );

    useEffect(() => {
        // 如果转码状态为成功、失败、部分成功，则500ms后隐藏进度条
        let timer = null;
        if (
            transcodingStatus === FJConstant.SUCCESS ||
            transcodingStatus === FJConstant.ERROR ||
            transcodingStatus === FJConstant.PARTIAL_SUCCESS ||
            transcodingStatus === FJConstant.SOURCE_LOADING_ERROR
        ) {
            timer = setTimeout(() => {
                setShouldShowProgress(false);
            }, 500);
        }
        return () => {
            clearTimeout(timer);
            setShouldShowProgress(true);
        };
    }, [transcodingStatus]);

    const getFileName = filename => {
        const containerWidth = 200;
        let { fileText } = FJUtil.getEspText(filename, 13, containerWidth - 5);
        return fileText;
    };

    return (
        <div className={styles.box}>
            <FJModalClose onClick={handleClose} />
            <div className={styles.content}>
                {(transcodingStatus === FJConstant.DEFAULT ||
                    transcodingStatus === FJConstant.RUNNING ||
                    shouldShowProgress) && (
                    <div className={styles.loading_box}>
                        <div className={styles.title}>{FJLocalStore._('transcoding')}</div>
                        <div className={styles.progress}>
                            <div className={styles.line} style={{ width: `${progress}%` }} />
                        </div>
                        <div className={styles.progress_text}>{FJLocalStore._('please-wait-for-a-moment')}</div>
                        <div className={styles.button_box}>
                            <FJButton
                                type={FJButtonType.DEFAULT}
                                className={styles.cancel_button}
                                text={FJLocalStore._('cancel')}
                                onClick={handleCancelTask}
                            />
                        </div>
                    </div>
                )}
                {transcodingStatus === FJConstant.SOURCE_LOADING_ERROR && !shouldShowProgress && (
                    <div className={styles.error_box}>
                        <div className={styles.error_icon_box}>
                            <FJErrorIcon className={styles.error_icon} />
                        </div>
                        <div className={styles.title}>{FJLocalStore._('transcoding-failed')}</div>
                        <div className={styles.error_text}>
                            {FJLocalStore._('file-transcoding-failed-please-re-upload-or-try-again')}
                        </div>
                        <div className={styles.button_box}>
                            <FJButton
                                type={FJButtonType.DEFAULT}
                                className={styles.upload_button}
                                text={FJLocalStore._('upload')}
                                onClick={handleReupload}
                            />
                            <FJButton
                                type={FJButtonType.GRADIENT}
                                className={styles.retry_button}
                                text={FJLocalStore._('retry')}
                                onClick={handleRestart}
                            />
                        </div>
                    </div>
                )}
                {transcodingStatus === FJConstant.ERROR && !shouldShowProgress && (
                    <div className={styles.error_box}>
                        <div className={styles.error_icon_box}>
                            <FJErrorIcon className={styles.error_icon} />
                        </div>
                        <div className={styles.title}>{FJLocalStore._('transcoding-failed')}</div>
                        <div className={styles.error_text}>
                            {FJLocalStore._('file-transcoding-failed-please-re-upload-or-try-again')}
                        </div>
                        <div className={styles.button_box}>
                            <FJButton
                                type={FJButtonType.DEFAULT}
                                className={styles.cancel_button}
                                text={FJLocalStore._('cancel')}
                                onClick={handleClose}
                            />
                            <FJButton
                                type={FJButtonType.GRADIENT}
                                className={styles.retry_button}
                                text={FJLocalStore._('upload')}
                                onClick={handleReupload}
                            />
                        </div>
                    </div>
                )}
                {transcodingStatus === FJConstant.PARTIAL_SUCCESS && !shouldShowProgress && (
                    <div className={classNames(styles.error_box, styles.multi_task_box)}>
                        <div className={styles.title}>{FJLocalStore._('file-transcoding-results')}</div>
                        <div className={styles.error_text}>
                            {FJLocalStore._(
                                'some-files-failed-to-be-transcoded-please-replace-the-files-and-upload-them-again',
                            )}
                        </div>
                        <div className={styles.task_list_section}>
                            <ul className={styles.task_list}>
                                {transcodingTasks.map(task => (
                                    <li className={styles.task_item} key={task.id || task.tempId}>
                                        <div className={styles.task_name}>
                                            {getFileName(task.fileName || FJLocalStore._('unknown-file'))}
                                        </div>
                                        <div className={styles.task_status}>
                                            {task.status === FJConstant.SUCCESS && (
                                                <div className={styles.success_icon_box}>
                                                    <FJCheckIcon className={styles.success_icon} />
                                                </div>
                                            )}
                                            {task.status === FJConstant.ERROR && (
                                                <div className={styles.failed_text}>{FJLocalStore._('failed')}</div>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className={styles.button_box}>
                            <FJButton
                                type={FJButtonType.GRADIENT}
                                className={styles.confirm_button}
                                text={FJLocalStore._('ok')}
                                onClick={onConfirm}
                            />
                        </div>
                    </div>
                )}
                {transcodingStatus === FJConstant.SUCCESS && !shouldShowProgress && (
                    <div className={styles.success_box}>
                        <div className={styles.title}>{FJLocalStore._('transcoding-success')}</div>
                        <div className={styles.icon_box}>
                            <FJCheckIcon className={styles.icon} />
                        </div>
                    </div>
                )}
                <input
                    className={styles.hidden_input}
                    type='file'
                    id='transcode-reupload'
                    {...inputOptions}
                    ref={reuploadRef}
                    onChange={handleReuploadChange}
                />
            </div>
        </div>
    );
};

FJCloudTranscodingModal.propTypes = {
    active: PropTypes.bool,
    onClose: PropTypes.func,
    transcodingStatus: PropTypes.string,
    progress: PropTypes.number,
    error: PropTypes.string,
    transcodingTasks: PropTypes.array,
    onCancelTask: PropTypes.func,
    onRestart: PropTypes.func,
    onConfirm: PropTypes.func,
    onReupload: PropTypes.func,
    showCancelButton: PropTypes.bool,
    showUploadButton: PropTypes.bool,
    showRetryButton: PropTypes.bool,
    inputOptions: PropTypes.object,
};

export default FJCloudTranscodingModal;
